import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const numbersSchema = z.object({
  eyebrow: z.string().max(80).optional(),
  title: z.string().min(1).max(200),
  subtitle: z.string().max(500).optional(),
  align: z.enum(['left', 'center']).default('left'),
  theme: z.enum(['light', 'dark']).default('light'),
  items: z
    .array(
      z.object({
        eyebrow: z.string().max(20).optional(),
        value: z.string().min(1).max(40),
        label: z.string().min(1).max(160),
      }),
    )
    .min(2)
    .max(8),
});

export type NumbersData = z.infer<typeof numbersSchema>;

export function NumbersRenderer({
  eyebrow,
  title,
  subtitle,
  align = 'left',
  theme = 'light',
  items,
}: NumbersData) {
  const safeItems = Array.isArray(items) ? items : [];
  const wrapperCls = `${theme === 'light' ? 'v5-light' : ''} v5-section v5-numbers-page`.trim();
  const headCls = `v5-section-head${align === 'left' ? ' v5-section-head-left' : ''}`;
  return (
    <section className={wrapperCls}>
      <div className="container">
        <div className={headCls}>
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-num-h2">{title}</h2>
          {subtitle ? <p className="v5-num-sub">{subtitle}</p> : null}
        </div>
        <div className="v5-num-grid">
          {safeItems.map((item, i) => (
            <div key={i} className="v5-num-card">
              <div className="v5-num-eyebrow">{item.eyebrow ?? String(i + 1).padStart(2, '0')}</div>
              <div className="v5-num-big">{item.value}</div>
              <div className="v5-num-lbl">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const numbersBlock: BlockDefinition<NumbersData> = {
  meta: {
    type: 'numbers',
    label: 'Cifras (v5)',
    description: 'Bloque de métricas/cifras con eyebrow 01/02/03 + número grande + etiqueta.',
    category: 'content',
    icon: 'bar-chart-2',
  },
  schema: numbersSchema,
  defaults: {
    eyebrow: '● Track record',
    title: 'Nuestra trayectoria en cifras',
    subtitle: 'Operaciones cerradas desde 2009.',
    align: 'left',
    theme: 'light',
    items: [
      { eyebrow: '01', value: '+200', label: 'operaciones cerradas' },
      { eyebrow: '02', value: '17 años', label: 'en el mercado' },
      { eyebrow: '03', value: '85%', label: 'tasa de éxito' },
      { eyebrow: '04', value: '€115M', label: 'volumen movilizado' },
    ],
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 80 },
    { name: 'title', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 500 },
    {
      name: 'align',
      label: 'Alineación cabecera',
      type: 'select',
      options: [
        { label: 'Izquierda', value: 'left' },
        { label: 'Centrado', value: 'center' },
      ],
    },
    {
      name: 'theme',
      label: 'Tema',
      type: 'select',
      options: [
        { label: 'Claro', value: 'light' },
        { label: 'Oscuro', value: 'dark' },
      ],
    },
    {
      name: 'items',
      label: 'Cifras',
      type: 'array',
      max: 8,
      itemFields: [
        { name: 'eyebrow', label: 'Eyebrow (ej. 01)', type: 'text', max: 20 },
        { name: 'value', label: 'Número', type: 'text', required: true, max: 40 },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 160 },
      ],
    },
  ],
  Renderer: NumbersRenderer,
};
